import { useCan } from "components";
import { useTypedQuery } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { AppointmentDetailsKeys } from "modules/AppointmentDetails/queryKeys";
import { ApiInstance } from "utils/Api";

import type { CarCheck, Intervention, InterventionID, Pin, PinID, QuestionResult, QuestionResultID, QuestionResultImage } from "models";

export interface QuestionResultLetteredImage extends QuestionResultImage {
  letterCode: string;
}

export interface QuestionResultWithLetteredImages extends QuestionResult {
  letteredImages: QuestionResultLetteredImage[];
}

export interface LetteredCheck extends CarCheck {
  question_results_with_lettered_images: QuestionResultWithLetteredImages[];
}

const LETTER_A = 97;

export const letterQuestionResults = (questionResults: QuestionResult[], counter = { current: LETTER_A }) =>
  questionResults.map(question => ({
    ...question,
    letteredImages: question.images?.filter(image => image.active).map(image => ({ ...image, letterCode: String.fromCharCode(counter.current++) })) ?? []
  }));

const isConnectedQuestion = (question: QuestionResult, question_result_id?: QuestionResultID | null, pin_id?: PinID) =>
  question.id === question_result_id || question.pin_history?.some(pin => pin.id === pin_id);

const letterChecks = (checks: CarCheck[], intervention_id?: InterventionID, question_result_id?: QuestionResultID | null, pin_id?: PinID) => {
  let currentLetter = LETTER_A;
  const letteredChecks: LetteredCheck[] = checks
    .filter(check => check.intervention_id === intervention_id || check.question_items?.some(question => isConnectedQuestion(question, question_result_id, pin_id)))
    .map(check => {
      const questionItems =
        check.intervention_id === intervention_id
          ? (check.question_items ?? [])
          : (check.question_items?.filter(question => isConnectedQuestion(question, question_result_id, pin_id)) ?? []);

      return {
        ...check,
        question_results_with_lettered_images: questionItems.map(question => ({
          ...question,
          letteredImages: question.images?.filter(image => image.active).map(image => ({ ...image, letterCode: String.fromCharCode(currentLetter++) })) ?? []
        }))
      };
    });

  return letteredChecks;
};

const selectConnectedChecks = (checks: CarCheck[], intervention?: Intervention, pin?: Pin) =>
  letterChecks(checks, intervention?.id, intervention?.question_result_id ?? pin?.question_result_id, pin?.id);

export const useConnectedChecks = (intervention?: Intervention, pin?: Pin) => {
  const canListChecks = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.CarChecks);

  return useTypedQuery({
    enabled: canListChecks,
    queryKey: AppointmentDetailsKeys.checks,
    queryFn: async ({ baseUrl, endpoint, params }) =>
      (await ApiInstance.post(endpoint, { appointment_id: intervention?.appointment_id ?? pin?.appointment_id, ...params }, baseUrl)).data.car_checks,
    select: (checks: CarCheck[]) => selectConnectedChecks(checks, intervention, pin)
  });
};
